import i18next from "i18next";
import { getAccessoriesFailure } from "src/store/actions/accessories";
import { authenticationMergeRequest, getDealSuccess } from "src/store/actions/deal";
import { toggleGarageDrawer } from "src/store/actions/garageDrawer";
import { setGarageCreated } from "src/store/actions/onlineShopper";
import { toggleModal } from "src/store/actions/openModal";
import { patchPaymentsRequest } from "src/store/actions/payments";
import { pollingSuccess } from "src/store/actions/poller";
import { openTestDriveModal } from "src/store/actions/testDrive";
import { toggleTradeInDrawer } from "src/store/actions/tradeIn";
import { openTradeIn, closeTradeIn } from "src/store/actions/tradeIn";
import { deleteVehicleFailure, postVehicleFailure } from "src/store/actions/vehicles";
import { ActionType } from "src/store/dataService/actions/utils/actionCreator";
import { OnlineShopperState } from "Types/state";
import { ToggleGPCAction } from "../actions/onlineShopper/openGPC";
import { SetSignIn } from "../actions/onlineShopper/signIn";
import { CloseSubModalAction, OpenSubModalAction } from "../actions/onlineShopper/subModal";
import {
    SET_VIEWING_SCP_FORM,
    SHOW_POWER_USER_TOOLS,
    SWITCH_MODAL_SIZE,
    UPDATE_AUTH,
    UPDATE_DEAL_VEHICLES,
    UPDATE_MODAL_SIZE,
    UPDATE_SRP_LINK,
} from "../constants";
import { CLOSE_SUB_MODAL, OPEN_SUB_MODAL, SET_SIGN_IN, TOGGLE_FLYOUT } from "../constants/onlineShopper";
import { CreateDealErrorAction } from "../dataService/actions/deal";
import { DealResponses } from "../dataService/types";

export const initialState: OnlineShopperState = {
    env: "prod",
    garageCreated: false,
    hasTradeIn: "no",
    hasTradeInEstimate: false,
    isAppOpen: false,
    isAuthed: false,
    isDrawerOpen: false,
    isFlyoutOpen: false,

    isViewingSoftCreditRatingForm: false,
    modalSize: "sm",
    routeSignIn: false,
    srpLink: "",
    subModal: {
        isOpen: false,
        type: undefined,
    },
    tradePendingOpen: false,
    tradeInOpen: false, //MTI Trade-In
    referer: "",
    pageType: null,
    toggledVin: undefined,
};

interface MissingTypes {
    type:
        | SET_VIEWING_SCP_FORM
        | UPDATE_DEAL_VEHICLES
        | DealResponses.PatchDealPersonSuccess
        | DealResponses.GetDealError
        | SHOW_POWER_USER_TOOLS
        | SWITCH_MODAL_SIZE
        | TOGGLE_FLYOUT
        | UPDATE_AUTH
        | UPDATE_MODAL_SIZE
        | UPDATE_SRP_LINK;
    data: any;
}

function onlineShopper(
    state: OnlineShopperState = initialState,
    action:
        | MissingTypes
        | OpenSubModalAction
        | CloseSubModalAction
        | ToggleGPCAction
        | SetSignIn
        | CreateDealErrorAction
        | ActionType<typeof pollingSuccess>
        | ActionType<typeof getDealSuccess>
        | ActionType<typeof toggleTradeInDrawer>
        | ActionType<typeof patchPaymentsRequest>
        | ActionType<typeof openTradeIn>
        | ActionType<typeof closeTradeIn>
        | ActionType<typeof getAccessoriesFailure>
        | ActionType<typeof toggleModal>
        | ActionType<typeof postVehicleFailure>
        | ActionType<typeof deleteVehicleFailure>
        | ActionType<typeof setGarageCreated>
        | ActionType<typeof authenticationMergeRequest>
        | ActionType<typeof toggleGarageDrawer>
        | ActionType<typeof openTestDriveModal>
): OnlineShopperState {
    switch (action.type) {
        case openTestDriveModal.type:
            return {
                ...state,
                isDrawerOpen: false,
            };
        case toggleModal.type:
            return {
                ...state,
                modalSize: action.payload.size ?? state.modalSize,
                isAppOpen: !state.isAppOpen,
                isDrawerOpen: !state.isAppOpen && state.isDrawerOpen,
                toggledVin: action.payload.vin ?? state.toggledVin,
                tradeInOpen: state.isAppOpen ? false : state.tradeInOpen,
            };

        case UPDATE_MODAL_SIZE:
            return {
                ...state,
                modalSize: action.data.size,
            };
        case SWITCH_MODAL_SIZE:
            return {
                ...state,
                modalSize: state.modalSize === "sm" ? "lg" : "sm",
            };
        case OPEN_SUB_MODAL:
            return {
                ...state,
                subModal: {
                    isOpen: true,
                    message: action.data.message,
                    refresh: action.data.refresh,
                    title: action.data.title,
                    type: action.data.modalType,
                    bankOfferId: action.data.bankOfferIndex,
                },
            };
        case CLOSE_SUB_MODAL:
            return {
                ...state,
                subModal: initialState.subModal,
            };
        case TOGGLE_FLYOUT:
            return {
                ...state,
                isFlyoutOpen: !state.isFlyoutOpen,
            };

        case toggleGarageDrawer.type:
            let updatedCreditFormState = state.isViewingSoftCreditRatingForm;
            // "Unview" the credit rating form when the drawer is being toggled closed.
            if (state.isDrawerOpen && state.isViewingSoftCreditRatingForm) {
                updatedCreditFormState = false;
            }
            return {
                ...state,
                isDrawerOpen: !state.isDrawerOpen,
                isViewingSoftCreditRatingForm: updatedCreditFormState,
            };
        case toggleTradeInDrawer.type:
            return {
                ...state,
                isDrawerOpen: !state.isDrawerOpen,
                tradePendingOpen: !state.tradePendingOpen,
            };
        case openTradeIn.type:
            return {
                ...state,
                tradeInOpen: true,
                isAppOpen: true,
                toggledVin: action.payload.vin,
            };
        case closeTradeIn.type:
            return {
                ...state,
                tradeInOpen: false,
            };
        case UPDATE_AUTH:
            return {
                ...state,
                isAuthed: action.data.isAuthed,
                routeSignIn: action.data.isAuthed ? false : state.routeSignIn,
            };
        case UPDATE_SRP_LINK:
            return {
                ...state,
                srpLink: action.data.srpLink,
            };
        case SET_SIGN_IN:
            return {
                ...state,
                routeSignIn: action.data.routeSignIn,
            };

        case DealResponses.CreateError:
            return {
                ...state,

                subModal: {
                    isOpen: true,
                    message: action.message,
                    refresh: !state.isAuthed,
                    title: i18next.t("Modal.BadDealApologiesModal.title"),
                    type: "customModal",
                },
            };

        case postVehicleFailure.type:
            if (action.payload.reason === "vehicle_exists") {
                return state;
            }

            return {
                ...state,
                subModal: {
                    isOpen: true,
                    ...(action.payload.reason === "vehicle_limit" && {
                        type: "vehicleLimitApologiesModal",
                    }),
                    ...(action.payload.reason === "unexpected_issue" && {
                        message: i18next.t("Modal.unexpectedIssueMessage"),
                        refresh: !state.isAuthed,
                        title: i18next.t("Modal.AddVehicleModal.title"),
                        type: "customModal",
                    }),
                },
            };

        case deleteVehicleFailure.type:
            if (!state.isAuthed) {
                return state;
            }

            return {
                ...state,

                subModal: {
                    isOpen: true,
                    message: i18next.t("Modal.unexpectedIssueMessage"),
                    title: i18next.t("Modal.RemoveVehicleModal.title"),
                    type: "customModal",
                },
            };

        case getAccessoriesFailure.type:
            return {
                ...state,

                subModal: {
                    isOpen: true,
                    message: i18next.t("Modal.unexpectedIssueMessage"),
                    title: i18next.t("Modal.GetAccessoriesModal.title"),
                    type: "customModal",
                },
            };
        case UPDATE_DEAL_VEHICLES:
            return {
                ...state,
            };
        case SET_VIEWING_SCP_FORM:
            return {
                ...state,
                isViewingSoftCreditRatingForm: action.data.isViewingSoftCreditRatingForm,
            };
        case DealResponses.GetDealError:
            return {
                ...state,
            };
        case pollingSuccess.type:
            return {
                ...state,
            };
        case setGarageCreated.type:
            return {
                ...state,
                garageCreated: true,
            };

        case authenticationMergeRequest.type:
            return {
                ...state,
                isAuthed: true,
                routeSignIn: false,
                garageCreated: action.payload.garageCreated,
            };

        default:
            return state;
    }
}

export default onlineShopper;
